/**
 * What the door will probably say, worked out from the planes in view before
 * anything is sent: a dashed hint on the target while something is carried.
 * It follows the engine's own rules (held planes block unless a displacing
 * World lets a higher rank through; pinned never moves; soft holds never
 * refuse and never protect, a copy's included; a copy of another World is
 * busy there, not here, and only its owner writes it; with seats, holders are
 * counted, and so, as the door counts them, are copies). It never lights a
 * bit and never decides: `explainClaim` or the door itself does. Pure.
 */

import type { Args, Json, PlaneStore, RecordStore, WorldMatter } from '../contracts.ts';
import { full, readSlots, slotsOf } from './masks.ts';
import { planeParts } from './planes.ts';
import { describeOf, occupiersOf, worldKey, type Describe, type OccupierRecord } from './records.ts';
import { positionOf, samePosition } from './relay-geom.ts';

export type Verdict = 'ok' | 'maybe' | 'no';

export interface Prediction {
  readonly verdict: Verdict;
  /** In the door's own words where it can be said, else why this is only a guess. */
  readonly why: string;
  /** Root holders that would refuse it. */
  readonly conflicts: readonly string[];
  /** Planes a displacing claim would push out. */
  readonly displaces: readonly string[];
}

export interface Judged {
  readonly matter: WorldMatter | undefined;
  readonly occupiers: readonly OccupierRecord[];
  readonly describe: Describe | null;
}

const maybe = (why: string): Prediction => ({ verdict: 'maybe', why, conflicts: [], displaces: [] });

/** An occupier as a body names it: an id, or a unique name. */
export const resolveOccupier = (occupiers: readonly OccupierRecord[], ref: Json | undefined): OccupierRecord | null => {
  if (typeof ref === 'number') return occupiers.find((o) => o.id === ref) ?? null;
  if (typeof ref !== 'string') return null;
  const named = occupiers.filter((o) => o.name === ref);
  return named.length === 1 ? named[0]! : null;
};

/** The holder an occupier stands for: itself for a holder, its holder for an event or series. */
const rootOf = (occupiers: readonly OccupierRecord[], o: OccupierRecord): OccupierRecord => {
  if (o.holder === null) return o;
  return occupiers.find((h) => h.name === o.holder && h.holder === null) ?? o;
};

/**
 * Does a claim at `rank` by `who` push out what `other` holds? (`Guardian.outranks`): never a
 * pinned holder; a lower rank always; an equal rank only under the `id` tie-break, to the lower id.
 */
export const outranks = (who: OccupierRecord, rank: number, other: OccupierRecord, tieBreak: string): boolean => {
  if (other.pinned) return false;
  if (other.rank !== rank) return other.rank < rank;
  return tieBreak === 'id' ? who.id < other.id : false;
};

export interface ClaimIntent {
  readonly who: Json | undefined;
  readonly key: number;
  readonly slots: bigint;
  readonly soft?: boolean;
  readonly rank?: number;
  /** A handover's giver: the receiver is judged as if the giver's claim were absent. */
  readonly without?: Json | undefined;
}

/** The door's word for anything a copy is asked to do: only its owner writes it. */
const foreign = (name: string): Prediction =>
  ({ verdict: 'no', why: `the plane belongs to ${name}; propose to its owner`, conflicts: [], displaces: [] });

export const predictClaim = (j: Judged, intent: ClaimIntent): Prediction => {
  const pos = j.matter?.byKey.get(intent.key);
  if (!j.matter || !pos) return maybe('that position is not in view');
  if (intent.slots === 0n || (intent.slots & ~full(j.matter.leafSlotBits)) !== 0n) return { verdict: 'no', why: `slots must fit in ${j.matter.leafSlotBits} bits`, conflicts: [], displaces: [] };
  const who = resolveOccupier(j.occupiers, intent.who);
  if (!who) return maybe('who that is, the Roster has not said');
  // The door writes a soft hold without judging it at all, even a copy's.
  if (intent.soft) return { verdict: 'ok', why: 'a soft hold is never judged, and reserves nothing', conflicts: [], displaces: [] };
  if (who.kind === 'foreign') return foreign(who.name);

  const mine = rootOf(j.occupiers, who);
  const without = intent.without === undefined ? null : resolveOccupier(j.occupiers, intent.without);
  const excluded = without ? rootOf(j.occupiers, without).id : null;
  const byId = new Map(j.occupiers.map((o) => [o.id, o]));
  const seats = j.describe?.seats ?? j.matter.seats;
  const blockers: { record: OccupierRecord; root: OccupierRecord; slots: bigint }[] = [];
  /** Copies holding some of these: never in the way of one seat, but counted against several. */
  const copies: { record: OccupierRecord; slots: bigint }[] = [];
  for (const [plane, mask] of pos.planes) {
    const { occupierId, role } = planeParts(plane);
    if (role !== 'held' || (mask & intent.slots) === 0n) continue;
    const record = byId.get(occupierId);
    const kind = record?.kind ?? j.matter.kinds.get(occupierId);
    if (kind === 'foreign' && seats === 1) continue;
    if (!record) return maybe('someone the Roster has not named yet holds some of these');
    if (kind === 'foreign') {
      if (record.id !== excluded) copies.push({ record, slots: mask & intent.slots });
      continue;
    }
    const root = rootOf(j.occupiers, record);
    if (root.id === mine.id || root.id === excluded) continue;
    blockers.push({ record, root, slots: mask & intent.slots });
  }

  if (seats > 1) {
    for (const slot of slotsOf(intent.slots)) {
      const bit = 1n << BigInt(slot);
      const holding = [...new Set([
        ...blockers.filter((b) => (b.slots & bit) !== 0n).map((b) => b.root.name),
        ...copies.filter((c) => (c.slots & bit) !== 0n).map((c) => c.record.name),
      ])];
      if (holding.length >= seats) {
        return { verdict: 'no', why: `${seats} seats, ${seats} open, already held by ${holding.join(', ')}`, conflicts: holding, displaces: [] };
      }
    }
    return { verdict: 'ok', why: 'a seat is open at every one of these', conflicts: [], displaces: [] };
  }
  if (blockers.length === 0) return { verdict: 'ok', why: 'nobody else holds these', conflicts: [], displaces: [] };
  // Whether a rank may displace here is the World's describe to say; until it has, only a pin is sure.
  if (!j.describe) {
    const pinned = [...new Set(blockers.filter((b) => b.record.pinned).map((b) => b.root.name))];
    return pinned.length > 0
      ? { verdict: 'no', why: `held by ${pinned.join(', ')}, pinned`, conflicts: pinned, displaces: [] }
      : maybe('whether this World lets a higher rank displace, its describe has not said');
  }

  const rank = intent.rank ?? who.rank;
  const tieBreak = j.describe?.tieBreak ?? 'incumbent';
  const displacing = j.describe?.allowDisplace === true;
  const losers = displacing ? blockers.filter((b) => outranks(who, rank, b.record, tieBreak)) : [];
  if (losers.length === blockers.length) {
    const names = [...new Set(losers.map((l) => l.record.name))];
    return { verdict: 'ok', why: `displaces ${names.join(', ')}`, conflicts: [], displaces: names };
  }
  const standing = blockers.filter((b) => !losers.includes(b));
  const conflicts = [...new Set(standing.map((b) => b.root.name))];
  const slots = standing.reduce((acc, b) => acc | b.slots, 0n);
  const list = slotsOf(slots);
  // The server's own sentence (describeRefusal): one slot "is", several "are".
  const held = list.length === 1 ? `slot ${list[0]} is` : `slots ${list.join(', ')} are`;
  return { verdict: 'no', why: `${held} held by ${conflicts.join(', ')}`, conflicts, displaces: [] };
};

/** Whether `who` holds (or softly holds) every one of `slots` at `key`. */
const holds = (j: Judged, whoRef: Json | undefined, key: number, slots: bigint, role: 'held' | 'intent'): boolean | null => {
  const pos = j.matter?.byKey.get(key);
  const who = resolveOccupier(j.occupiers, whoRef);
  if (!pos || !who) return null;
  const mask = pos.planes.get(`${who.id}|${role}`) ?? 0n;
  return (mask & slots) === slots;
};

// -- from an operation and its arguments --------------------------------------------------

const bodyOf = (args: Args): Record<string, Json> => {
  const b = args.body;
  return b && typeof b === 'object' && !Array.isArray(b) ? b : {};
};

/** The key of the position a body names, when it is in view. */
const keyOf = (matter: WorldMatter | undefined, body: Record<string, Json>): number | undefined => {
  if (!matter) return undefined;
  let want: { parametric: number; coordinates: readonly number[] } | null = null;
  if (typeof body.date === 'string') want = positionOf({ date: body.date, ...(typeof body.hour === 'number' ? { hour: body.hour } : {}) });
  else if (typeof body.parametric === 'number' && Array.isArray(body.coordinates)) {
    want = { parametric: body.parametric, coordinates: body.coordinates.filter((c): c is number => typeof c === 'number') };
  }
  if (!want) return undefined;
  for (const pos of matter.byKey.values()) if (samePosition(pos, want)) return pos.key;
  return undefined;
};

export const judgedFrom = (world: string, planes: PlaneStore, records: RecordStore): Judged => ({
  matter: planes.get(world),
  occupiers: occupiersOf(records.get(world, 'occupiers')),
  describe: describeOf(records.get(world, 'describe')),
});

/**
 * The hint for a drop, from the operation and the arguments it would send:
 * what a Verb's `preview` returns. Operations with nothing to foresee are
 * `maybe`.
 */
export const predict = (op: string, args: Args, ctx: { planes: PlaneStore; records: RecordStore }, persona: 'A' | 'B' | 'C' = 'A'): Prediction => {
  const w = args.w;
  if (typeof w !== 'string') return maybe('no World named');
  const j = judgedFrom(worldKey(persona, w), ctx.planes, ctx.records);
  const body = bodyOf(args);
  const slots = readSlots(body.slots);
  const key = keyOf(j.matter, body);
  if (slots === null) return maybe('no slots yet');
  if (key === undefined) return maybe('that position is not in view');
  switch (op) {
    case 'claim':
    case 'explainClaim':
      return predictClaim(j, { who: body.who, key, slots, soft: op === 'claim' && body.soft === true, ...(typeof body.rank === 'number' ? { rank: body.rank } : {}) });
    // A confirm is a firm claim that then lets the soft hold go: the door judges it as one.
    case 'confirm':
      return predictClaim(j, { who: body.who, key, slots });
    case 'handover': {
      const from = resolveOccupier(j.occupiers, body.from);
      if (from?.kind === 'foreign') return foreign(from.name);
      const has = holds(j, body.from, key, slots, 'held');
      if (has === false) {
        return { verdict: 'no', why: `${from?.name ?? 'the giver'} does not hold all of these`, conflicts: from ? [from.name] : [], displaces: [] };
      }
      return predictClaim(j, { who: body.to, key, slots, without: body.from });
    }
    case 'release': {
      const who = resolveOccupier(j.occupiers, body.who);
      if (who?.kind === 'foreign') return foreign(who.name);
      const has = holds(j, body.who, key, slots, 'held');
      return has === false ? maybe('holds none or only some of these; the rest is a no-op') : { verdict: 'ok', why: 'giving back what it holds', conflicts: [], displaces: [] };
    }
    case 'giveUp': {
      const has = holds(j, body.who, key, slots, 'intent');
      return has === false ? maybe('no soft hold on all of these') : { verdict: 'ok', why: 'letting a soft hold go', conflicts: [], displaces: [] };
    }
    case 'queue':
      return { verdict: 'ok', why: 'a place in line; a soft hold that waits', conflicts: [], displaces: [] };
    default:
      return maybe('nothing to foresee');
  }
};
